import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import {
  eurIsPositive,
  formatEurDashboard,
  formatKcDashboard,
  type ClosedMonthRow,
} from "../../schemas/tenantDashboard";

/**
 * UC-005 ClosedMonthsTable — uzavřené měsíce A&L soft pro David (Phase 23).
 *
 * Desktop (≥ md) → DataTable s sloupci Měsíc | Středisko | CZK | EUR | SPZ |
 *   Transakce | Status (composite PDF available + statement_set_complete) +
 *   Akce (Stáhnout PDF).
 * Mobile (< md) → Card layout (REQ-UI-015 mobile-first floor).
 *
 * REQ-BIZ-022 + princip #4 sync transparency — "Stáhnout PDF" button je
 * disabled pokud `statementSet.complete = false` nebo `attached_invoice_statement_count = 0`.
 * Tlačítko UI ukazuje důvod (helper text), aby uživatel věděl proč nejde
 * (princip #4 transparency, ne tichý disable).
 *
 * UC-005 EC mapping: cross-tenant attempt na cizí budget_id URL → backend
 * vrací 403 + TENANT_ACCESS_DENIED (REQ-EH-007 + princip #6 RLS). Frontend
 * tu chybu surfacuje přes apiCall standardní error path; tato komponenta jen
 * renderuje server data.
 */

export interface ClosedMonthsTableProps {
  rows: ClosedMonthRow[];
  loading?: boolean;
  /** Callback při kliku na "Stáhnout PDF" — předáno budget_id. */
  onDownloadPdf?: (row: ClosedMonthRow) => void;
  /** Override breakpoint pro testy. */
  forceMode?: "desktop" | "mobile";
}

function pdfButtonEnabled(row: ClosedMonthRow): boolean {
  return (
    row.statement_set_complete &&
    row.attached_invoice_statement_count > 0 &&
    row.composite_pdf_id !== null
  );
}

function pdfDisabledReason(row: ClosedMonthRow): string {
  if (!row.statement_set_complete) {
    return "Vyúčtování čeká na dodání. Stahování bude dostupné, jakmile bude úplné.";
  }
  if (row.attached_invoice_statement_count === 0) {
    return "Žádné vyúčtování dosud nepřiloženo.";
  }
  if (row.composite_pdf_id === null) {
    return "Composite PDF se ještě generuje.";
  }
  return "";
}

export function ClosedMonthsTable({
  rows,
  loading,
  onDownloadPdf,
  forceMode,
}: ClosedMonthsTableProps) {
  const breakpoint = useBreakpoint();
  const mode: "desktop" | "mobile" =
    forceMode ?? (breakpoint === "mobile" ? "mobile" : "desktop");

  if (mode === "mobile") {
    return (
      <ClosedMonthsCardList
        rows={rows}
        loading={loading}
        onDownloadPdf={onDownloadPdf}
      />
    );
  }

  const columns: Column<ClosedMonthRow>[] = [
    {
      key: "month",
      header: "Měsíc",
      cell: (r) => <strong>{r.month}</strong>,
    },
    {
      key: "billing_group",
      header: "Středisko",
      cell: (r) => r.billing_group_code,
    },
    {
      key: "total_kc",
      header: "CZK",
      cell: (r) => formatKcDashboard(r.total_kc),
    },
    {
      key: "total_eur",
      header: "EUR",
      cell: (r) => (eurIsPositive(r.total_eur) ? formatEurDashboard(r.total_eur) : "—"),
    },
    {
      key: "spz_count",
      header: "SPZ",
      cell: (r) => r.spz_count,
    },
    {
      key: "transaction_count",
      header: "Transakce",
      cell: (r) => r.transaction_count,
    },
    {
      key: "status",
      header: "Stav vyúčtování",
      cell: (r) =>
        r.statement_set_complete ? (
          <Badge color="success">Úplné</Badge>
        ) : (
          <Badge color="warning">Čeká</Badge>
        ),
    },
    {
      key: "actions",
      header: "Akce",
      cell: (r) => {
        const enabled = pdfButtonEnabled(r);
        return (
          <span title={enabled ? undefined : pdfDisabledReason(r)}>
            <Button
              color="secondary"
              size="sm"
              isDisabled={!enabled}
              onPress={enabled && onDownloadPdf ? () => onDownloadPdf(r) : undefined}
              data-testid={`closed-month-download-${r.budget_id}`}
            >
              Stáhnout PDF
            </Button>
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3" data-testid="closed-months-table" data-mode="desktop">
      <DataTable
        caption="Uzavřené měsíce A&L soft"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.budget_id}
        emptyMessage="Žádné uzavřené měsíce v zvoleném období."
        loading={loading}
      />
    </div>
  );
}

interface ClosedMonthsCardListProps {
  rows: ClosedMonthRow[];
  loading?: boolean;
  onDownloadPdf?: (row: ClosedMonthRow) => void;
}

function ClosedMonthsCardList({
  rows,
  loading,
  onDownloadPdf,
}: ClosedMonthsCardListProps) {
  if (loading) {
    return (
      <div
        className="m-0 flex list-none flex-col gap-3 p-0"
        data-testid="closed-months-table"
        data-mode="mobile"
      >
        <p className="p-8 text-center text-tertiary">Načítám…</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        className="m-0 flex list-none flex-col gap-3 p-0"
        data-testid="closed-months-table"
        data-mode="mobile"
      >
        <p className="p-8 text-center text-tertiary">Žádné uzavřené měsíce v zvoleném období.</p>
      </div>
    );
  }
  return (
    <ul
      className="m-0 flex list-none flex-col gap-3 p-0"
      data-testid="closed-months-table"
      data-mode="mobile"
      role="list"
    >
      {rows.map((r) => {
        const enabled = pdfButtonEnabled(r);
        return (
          <li
            key={r.budget_id}
            className="flex flex-col gap-2 rounded-xl bg-primary px-4 py-3.5 shadow-xs ring-1 ring-secondary"
            data-testid={`closed-month-card-${r.budget_id}`}
          >
            <header className="flex flex-wrap items-center gap-2">
              <strong>{r.month}</strong>
              <span className="text-sm text-tertiary">{r.billing_group_code}</span>
              {r.statement_set_complete ? (
                <Badge color="success">Úplné</Badge>
              ) : (
                <Badge color="warning">Čeká</Badge>
              )}
            </header>
            <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <dt className="text-xs font-semibold tracking-[0.02em] text-tertiary uppercase">CZK</dt>
                <dd className="m-0 font-semibold">{formatKcDashboard(r.total_kc)}</dd>
              </div>
              {eurIsPositive(r.total_eur) && (
                <div>
                  <dt className="text-xs font-semibold tracking-[0.02em] text-tertiary uppercase">EUR</dt>
                  <dd className="m-0 font-semibold">{formatEurDashboard(r.total_eur)}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-semibold tracking-[0.02em] text-tertiary uppercase">SPZ</dt>
                <dd className="m-0 font-semibold">{r.spz_count}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold tracking-[0.02em] text-tertiary uppercase">Transakce</dt>
                <dd className="m-0 font-semibold">{r.transaction_count}</dd>
              </div>
            </dl>
            <footer className="flex flex-col items-start gap-1.5">
              <Button
                color="secondary"
                size="sm"
                isDisabled={!enabled}
                onPress={enabled && onDownloadPdf ? () => onDownloadPdf(r) : undefined}
                data-testid={`closed-month-download-${r.budget_id}`}
              >
                Stáhnout PDF
              </Button>
              {!enabled && (
                <p className="m-0 text-[0.8125rem] text-tertiary">
                  {pdfDisabledReason(r)}
                </p>
              )}
            </footer>
          </li>
        );
      })}
    </ul>
  );
}
